import urlConstants from '~/lib/constants/url.constants';
import { MaiFeedbackRequest } from '~/lib/types/mai/MaiFeedbackRequest';
import { MaiFeedbackResponse } from '~/lib/types/mai/MaiFeedbackResponse';
import { HttpRequestMethod } from '~/store';

import {
  MaiMessage,
  MaiSuggestedPrompt,
} from '~/components/maud/patients/profile/tabs/overviews/MaiChat/maiChat.types';
import {
  MaiHistoryRow,
  mapHistoryToMessages,
} from '~/components/maud/patients/profile/tabs/overviews/MaiChat/maiChatHistory.util';

import api from './api';
import { CacheTag } from './CacheTag';
import { ENUMERATION_CACHE_TIME } from './enumerations';

export const MAI_HISTORY_DAYS = 7;

export interface MaiMessagesRequest {
  patientId: string;
  days?: number;
}

// Single source for the getMaiMessages cache key: the streaming writer and the
// panel reader must land on the same entry.
export const maiHistoryArgs = (patientId: string): MaiMessagesRequest => ({
  patientId,
  days: MAI_HISTORY_DAYS,
});

export interface MaiMessagesResponse {
  messages: MaiHistoryRow[];
}

export interface MaiPromptsRequest {
  patientId: string;
}

export interface MaiPromptsResponse {
  prompts: MaiSuggestedPrompt[];
  catalog_version: string;
}

export const maiApi = api.injectEndpoints({
  endpoints: (build) => ({
    submitMaiFeedback: build.mutation<MaiFeedbackResponse, MaiFeedbackRequest>({
      query: (body) => ({
        body,
        method: HttpRequestMethod.POST,
        url: urlConstants.mai.feedback,
      }),
    }),
    retractMaiFeedback: build.mutation<void, string>({
      query: (messageId) => ({
        method: HttpRequestMethod.DELETE,
        url: urlConstants.mai.retractFeedback(messageId),
      }),
    }),
    getMaiMessages: build.query<MaiMessage[], MaiMessagesRequest>({
      query: ({ patientId, days = MAI_HISTORY_DAYS }) => ({
        method: HttpRequestMethod.GET,
        url: urlConstants.mai.messages,
        params: {
          patient_id: patientId,
          days,
        },
      }),
      transformResponse: (response: MaiMessagesResponse): MaiMessage[] =>
        mapHistoryToMessages(response.messages),
      keepUnusedDataFor: Infinity,
      providesTags: (_result, _error, { patientId }) => [
        { id: patientId, type: CacheTag.MaiMessages },
      ],
    }),
    getMaiPrompts: build.query<MaiPromptsResponse, MaiPromptsRequest>({
      query: ({ patientId }) => ({
        method: HttpRequestMethod.GET,
        url: urlConstants.mai.prompts,
        params: { patient_id: patientId },
      }),
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
      providesTags: (_result, _error, { patientId }) => [
        { id: patientId, type: CacheTag.MaiPrompts },
      ],
    }),
  }),
});

export const {
  useSubmitMaiFeedbackMutation,
  useRetractMaiFeedbackMutation,
  useGetMaiMessagesQuery,
  useGetMaiPromptsQuery,
} = maiApi;
